import { useState, useEffect, useCallback, useRef } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'iwanna_chat_session';
const MESSAGES_KEY = 'iwanna_chat_messages';

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s${Date.now()}${Math.random().toString(36).slice(2, 10)}`);

export const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = newId();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const loadMessages = () => {
  try { return JSON.parse(sessionStorage.getItem(MESSAGES_KEY) || '[]'); } catch { return []; }
};

export const useChat = (welcome) => {
  const [messages, setMessages] = useState(loadMessages);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (welcome && messages.length === 0) {
      setMessages([{ id: 'welcome', role: 'assistant', content: welcome, partners: [] }]);
    }
  }, [welcome, messages.length]);

  useEffect(() => { sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(messages)); }, [messages]);

  const patchLast = useCallback((fn) => setMessages((prev) => prev.map((m, i) => (i === prev.length - 1 ? fn(m) : m))), []);

  const send = useCallback(async (text, articulo) => {
    const content = text.trim();
    if (!content || sending) return;
    setError('');
    setSending(true);
    setMessages((prev) => [...prev, { id: newId(), role: 'user', content }, { id: newId(), role: 'assistant', content: '', partners: [], streaming: true }]);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: content, articulo: articulo || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'No pudimos responder ahora.');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.delta) patchLast((m) => ({ ...m, content: m.content + evt.delta }));
          if (evt.done) patchLast((m) => ({ ...m, partners: evt.partners || [], streaming: false }));
        }
      }
      patchLast((m) => ({ ...m, streaming: false }));
    } catch (err) {
      const msg = err.message || 'No pudimos responder ahora.';
      setError(msg);
      patchLast((m) => (m.content ? { ...m, streaming: false } : { ...m, content: msg, streaming: false, failed: true }));
    } finally {
      setSending(false);
    }
  }, [sending, patchLast]);

  return { messages, send, sending, error, sessionId: sessionId.current };
};
